import { computed, ref } from "vue";

// Shared state for canvas view mode
export const isFullscreen = ref(false);

export function setFullscreen(value: boolean) {
  isFullscreen.value = value;
}

/**
 * Aide contextuelle affichée (cartouche en bas à droite, cf. ToolHintBar). Les
 * volets de droite s'en servent pour décaler leur pied, qu'elle masquerait.
 */
export const isToolHintVisible = ref(false);

/**
 * Pointeurs actuellement posés, suivis par `pointerId`. Un booléen ne peut pas
 * représenter le multitouch : il repasserait à `false` dès le PREMIER
 * relâchement, alors qu'un pincement laisse encore un doigt sur l'écran.
 *
 * Deux ensembles parce qu'il y a deux questions distinctes :
 * - `canvasPointers` — les gestes commencés SUR le canvas. Pilote la classe
 *   `drawing-active`, qui masque les barres d'outils (opacité 0.3 +
 *   `pointer-events: none`) : la passer à « n'importe quelle interaction » ferait
 *   disparaître une barre d'outils sous le doigt qui la touche.
 * - `activePointers` — tout pointeur posé, canvas ou non. Répond à « l'utilisateur
 *   est-il en train de faire quelque chose ? », ce qui inclut la manipulation d'un
 *   menu.
 */
const canvasPointers = ref(new Set<number>());
const activePointers = ref(new Set<number>());

/** jsdom (tests) n'émet pas toujours de vrais `PointerEvent`. */
function pointerIdOf(e: Event): number {
  return (e as PointerEvent).pointerId ?? 0;
}

/** Au moins un doigt/stylet posé sur le canvas (geste de dessin en cours). */
export const isCanvasDrawing = computed(() => canvasPointers.value.size > 0);

/**
 * Au moins un pointeur posé, n'importe où dans l'app. Source unique pour « ne pas
 * interrompre l'utilisateur » — cf. la sauvegarde automatique, qui ne doit jamais
 * faire surgir son overlay bloquant pendant un geste.
 */
export const isUserInteracting = computed(() => activePointers.value.size > 0);

/**
 * À câbler sur le `@pointerdown` du conteneur canvas : c'est ce qui distingue un
 * geste de dessin d'une interaction ailleurs dans l'interface. Le relâchement est
 * géré au niveau `window` (cf. `initPointerTracking`).
 */
export function startCanvasPointer(e: Event) {
  // Clic droit (ou milieu) à la souris : pas un tracé. Sur macOS, le menu
  // contextuel natif avale le pointerup, et les barres resteraient estompées.
  const { pointerType, button } = e as PointerEvent;
  if (pointerType === "mouse" && button !== 0) return;
  canvasPointers.value.add(pointerIdOf(e));
}

function onWindowPointerDown(e: Event) {
  activePointers.value.add(pointerIdOf(e));
}

function onWindowPointerRelease(e: Event) {
  const id = pointerIdOf(e);
  canvasPointers.value.delete(id);
  activePointers.value.delete(id);
}

/**
 * Le relâchement s'écoute sur `window`, jamais sur le conteneur canvas : le code
 * n'utilise nulle part `setPointerCapture`, donc un doigt parti du canvas et
 * relâché au-dessus d'une barre d'outils n'émettrait aucun `pointerup` sur le
 * conteneur — le pointeur resterait éternellement « posé ».
 */
export function initPointerTracking() {
  clearPointers();
  window.addEventListener("pointerdown", onWindowPointerDown);
  window.addEventListener("pointerup", onWindowPointerRelease);
  window.addEventListener("pointercancel", onWindowPointerRelease);
}

export function disposePointerTracking() {
  window.removeEventListener("pointerdown", onWindowPointerDown);
  window.removeEventListener("pointerup", onWindowPointerRelease);
  window.removeEventListener("pointercancel", onWindowPointerRelease);
  clearPointers();
}

/**
 * Purge défensive. Passer en arrière-plan au milieu d'un geste est le cas typique
 * où le `pointerup` ne sera jamais distribué : sans ça, un identifiant fantôme
 * laisserait l'app croire indéfiniment que l'utilisateur a le doigt posé.
 */
export function clearPointers() {
  canvasPointers.value.clear();
  activePointers.value.clear();
}

export const isDraggableVertical = ref(false);

export function toggleDraggableVertical() {
  isDraggableVertical.value = !isDraggableVertical.value;
}
